import { motion } from 'framer-motion'
import { StyleFeatureCard, StyleFeatureCardGrid } from './style'
import { StyleText, StyleTextWrapper } from '@pages/front/commons/Text/style'
import { IFeatureService } from '@pages/front/types/type'
import {
  BookLabTestIcon,
  CertifiedMedicineIcon,
  DoctorSupportIcon,
  InstantConsultationIcon,
  VisitClinicIcon,
} from '@pages/front/svgs/svg'
import { StyleImageWrapper } from '@pages/front/commons/ImageWrapper/style'
import { StyleContentWrapper } from '@pages/front/commons/ContentWrapper/style'
import { TextWrapper } from '@pages/front/commons/Text'

// Create motion-wrapped components
const MotionFeatureCard = motion(StyleFeatureCard)
const MotionFeatureCardGrid = motion(StyleFeatureCardGrid)
const MotionText = motion(StyleText)
const MotionImageWrapper = motion(StyleImageWrapper)

const FeatureServices: IFeatureService[] = [
  {
    icon: DoctorSupportIcon,
    serviceTitle: 'Online Doctor Support',
    serviceDescription: `"Get personalized medical advice without leaving your home. Connect with licensed doctors anytime you need support, whether it's a quick question or a full consultation."`,
    bgColor: '#e4f7ff',
  },
  {
    icon: VisitClinicIcon,
    serviceTitle: 'Visit Clinic',
    serviceDescription: `"Prefer an in-person consultation? Book a clinic visit
near you with ease. Get face-to-face care from
trusted professionals at your convenience."`,
    bgColor: '#e8ffe7',
  },
  {
    icon: InstantConsultationIcon,
    serviceTitle: 'Instant Consultation',
    serviceDescription: `"Health concerns can't always wait - and now they don't
have to. Start a consultation instantly and speak with a
doctor who's ready to help."`,
    bgColor: '#fdeaf0',
  },
  {
    icon: BookLabTestIcon,
    serviceTitle: 'Book Lab Tests',
    serviceDescription: `"Easily schedule lab tests from the comfort of your
home or visit a nearby diagnostic center. Fast
booking, accurate results, and trusted partners - all
in one place."`,
    bgColor: '#f2efff',
  },
  {
    icon: CertifiedMedicineIcon,
    serviceTitle: 'Certified Medicines',
    serviceDescription: `"Healthcare made simpler - browse, select, and
order authentic medicines in just a few taps. Backed
by licensed pharmacies and quick doorstep
delivery."`,
    bgColor: '#fef4dd',
  },
]
const title = 'All-In-One Care Solutions'
const subtitle = `"Skip the hassle of multiple platforms. Access virtual consultations, diagnostics,
prescriptions, and more — all in one seamless experience."`

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      when: 'beforeChildren',
    },
  },
}

const cardVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
}

const specialCardVariants = {
  hidden: { scale: 0.95, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: 'backOut',
    },
  },
}

const FeaturedCard = () => {
  return (
    <StyleContentWrapper>
      <TextWrapper title={title} subtitle={subtitle} textWidth={'600'} />

      <MotionFeatureCardGrid
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
      >
        {FeatureServices.map((service, i) => {
          if (i === 2) {
            return (
              <MotionFeatureCard
                key={i}
                bgColor={service.bgColor}
                variants={specialCardVariants}
                whileHover={{ y: -5 }}
              >
                <motion.div
                  dangerouslySetInnerHTML={{ __html: service.icon }}
                />
                <MotionText fontSize="20">{service.serviceTitle}</MotionText>
                <MotionText color="text" weight="500" fontSize="16">
                  {service.serviceDescription}
                </MotionText>
                <MotionImageWrapper
                  direction="center"
                  widthsm="100%"
                  whileHover={{ scale: 1.03 }}
                >
                  <img src="/phones/instantConsultationImage.png" alt="" />
                </MotionImageWrapper>
              </MotionFeatureCard>
            )
          }

          return (
            <MotionFeatureCard
              key={i}
              bgColor={service.bgColor}
              variants={cardVariants}
              whileHover={{ y: -5 }}
            >
              <motion.div dangerouslySetInnerHTML={{ __html: service.icon }} />
              <MotionText fontSize="20">{service.serviceTitle}</MotionText>
              <MotionText color="text" weight="500" fontSize="16">
                {service.serviceDescription}
              </MotionText>
            </MotionFeatureCard>
          )
        })}
      </MotionFeatureCardGrid>
    </StyleContentWrapper>
  )
}

export default FeaturedCard
